/**
 * What the self-funding projection offers, and where its Back goes.
 *
 * Reached through the sequence: finish with self-funding, or explore insurance options. Reached from
 * the options' "I want to self-fund the entire event" (`returnTo`), the consumer has already said how
 * they want to pay, so the screen drops its insurance prompts; its primary action takes them back to the
 * protection options, with the planning session beside it. Back retraces the way here: the account steps, or the
 * paywall when it went straight to the projection (`returnTo=self-fund`).
 */
import { endGuidedJourney } from '../lib/guided-journey';
import { LTC_PLANNING_SESSION_LABEL, LTC_PLANNING_SESSION_URL } from '../lib/planning-session';
import { useConsumerFlow } from './use-consumer-flow';
import { useFinishWithSelfFunding } from './use-finish-with-self-funding';

export function useProjectionChoices(clientId: string) {
  const { goTo, goWithin, returnTo } = useConsumerFlow(clientId);
  const { finishAction } = useFinishWithSelfFunding(clientId);
  const isSelfFundChoice = returnTo === 'options' || returnTo === 'self-fund';

  return {
    isSelfFundChoice,
    continueActions: isSelfFundChoice
      ? [
          { label: 'Show Me Efficient Protection Options', onSelect: () => goTo('options') },
          {
            label: LTC_PLANNING_SESSION_LABEL,
            onSelect: () => window.open(LTC_PLANNING_SESSION_URL, '_blank', 'noopener,noreferrer'),
          },
        ]
      : [
          finishAction,
          {
            label: 'Explore insurance options',
            onSelect: () => {
              endGuidedJourney(clientId);
              goTo('payment-preference');
            },
          },
        ],
    back: () =>
      returnTo === 'self-fund'
        ? goTo('self-fund', { returnTo: 'options' })
        : goWithin('assessment-accounts'),
  };
}
